import React from 'react';
import { Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import NotesSearchBar from './NotesSearchBar';

const NotesTable = ({
  paginatedNotes,
  filteredNotes,
  allNotes,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  totalPages,
  itemsPerPage,
  controle
}) => {
  
  const getMentionColor = (mention) => {
    const MENTION_COLORS = {
      'Très Bien': '#10B981',
      'Bien': '#3B82F6', 
      'Assez Bien': '#F59E0B',
      'Passable': '#F97316',
      'Insuffisant': '#EF4444',
      'Très Insuffisant': '#DC2626'
    };
    return MENTION_COLORS[mention] || '#6B7280';
  };

  const getNoteColor = (note) => {
    if (note >= 16) return 'text-green-600 bg-green-50';
    if (note >= 14) return 'text-blue-600 bg-blue-50';
    if (note >= 12) return 'text-yellow-600 bg-yellow-50';
    if (note >= 10) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Eye className="h-5 w-5 mr-2" />
            Liste des notes ({filteredNotes.length} étudiants{searchTerm && ` sur ${allNotes.length}`})
          </h3>
          <NotesSearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setCurrentPage={setCurrentPage}
            filteredNotes={filteredNotes}
            controle={controle}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Étudiant
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Note (/20)
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mention
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Observation
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Saisi par
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedNotes.map((note, index) => (
              <tr key={note.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#202654] to-[#202654]/80 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {note.etudiant.nom.charAt(0)}{note.etudiant.prenom.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {note.etudiant.nom} {note.etudiant.prenom}
                      </div>
                      <div className="text-sm text-gray-500">
                        {note.etudiant.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getNoteColor(note.note)}`}>
                    {note.note}/20
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <span 
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: getMentionColor(note.mention) }}
                  >
                    {note.mention}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {note.observation || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="text-sm text-gray-500">
                    {note.renseigner_par_username || 'Non spécifié'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(note.create_at).toLocaleDateString('fr-FR')}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && filteredNotes.length > itemsPerPage && (
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Affichage de {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, filteredNotes.length)} sur {filteredNotes.length} étudiants
              {searchTerm && <span className="text-gray-500"> (filtrés sur {allNotes.length} total)</span>}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </button>
              
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                        currentPage === page
                          ? 'bg-[#202654] text-white shadow-sm'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesTable; 